/* eslint no-control-regex:0 */
import locale from '@glu/locale';
import validationProcessor from './validationProcessor';

const isNullOrUndefined = value => value === null || typeof value === 'undefined';

const patterns = {
  alphaNumeric: /^[a-zA-Z0-9]*$/,

  // Matches 12345 or 12345-6789 (US zip code)
  usZip: /^\d{5}(?:-\d{4})?$/,

  // Matches 1234567890, (123)456-7890, 123-456-7890, 123.456.7890, but not 12-3456-7890, etc.
  us10DigitPhone: /^(?:\(\d{3}\)\s?|\d{3}[.\s-]?)\d{3}[\s.-]?\d{4}$|^\d*$/,

  // obtained from http://thedersen.com/projects/backbone-validation version 0.9.1
  emailAddress: /^((([a-z]|\d|[!#$%&'*+\-/=?^_`{|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#$%&'*+\-/=?^_`{|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))$/i,

  date: /^(1[0-2]|0?[1-9])\/((0[1-9])|([12][0-9])|(3[01]))\/([12][7890]\d{2})$/,

  numeralsOnly: /^\d*$/
};

// Determines whether or not a value is empty
function hasValue(value) {
  return !(isNullOrUndefined(value) || ((typeof value === 'string') && value.replace(/^\s+|\s+$/gm, '').length < 1) || (Array.isArray(value) && !value.length));
}

const validators = {
  /**
   * Messages are defined in this hash and must EXACTLY match the name of the validator
   * function for which they are supplying a message.
   *
   * The placeholders, such as "{0}" are replaced with:
   *        0   - The name of the field that is being validated
   *        1-N - The parameters passed in the param field of the validator as specified
   *              in the validator spec property. Note that to get more than one parameter
   *              in you should use an array to ensure an exact order.
   */
  initMessages: function initBuiltInMessages() {
    return {
      // messages, must match the function names for Validators.functions EXACTLY
      alphaNumeric: locale.get('validator.alphaNumeric'),
      arrayNotEmpty: locale.get('validator.arrayNotEmpty'),
      required: locale.get('validator.required'),
      usZipCode: locale.get('validator.usZipCode'),
      us10DigitPhone: locale.get('validator.us10DigitPhone'),
      emailAddress: locale.get('validator.emailAddress'),
      minLength: locale.get('validator.minLength'),
      maxLength: locale.get('validator.maxLength'),
      minValue: locale.get('validator.minValue'),
      minValueExclusive: locale.get('validator.minValueExclusive'),
      maxValue: locale.get('validator.maxValue'),
      maxValueExclusive: locale.get('validator.maxValueExclusive'),
      isNumeric: locale.get('validator.isNumeric'),
      isNumeralsOnly: locale.get('validator.isNumeralsOnly')
    };
  },

  /* messages are to be set by the implementing subclass after all locale bundles have been loaded.
   * If you have no custom messages just set the result of initMessages above */
  messages: {},

  // Global validation functions
  functions: {
    alphaNumeric(key, enabled, data) {
      if (!enabled) {
        return true;
      }

      return patterns.alphaNumeric.test(data[key]);
    },

    // this method validates that an array is not empty
    // except in the case where the parameter evaluates to false
    arrayNotEmpty(key, enabled, data) {
      if (!enabled) {
        return true;
      }

      const value = data[key];

      if (!value || !value.length) {
        return false;
      }

      return value.length > 1 || (value[0] !== null && value[0] !== undefined);
    },

    emailAddress(key, enabled, data) {
      if (!enabled) {
        return true;
      }

      return !data[key] || patterns.emailAddress.test(data[key]);
    },

    isNumeralsOnly(key, enabled, data) {
      if (!enabled) {
        return true;
      }

      const value = data[key];
      return !value || patterns.numeralsOnly.test(value);
    },

    isNumeric(key, enabled, data) {
      let value = data[key];

      if (!enabled || !value || typeof value === 'number') {
        return true;
      }

      if (typeof value === 'string') {
        value = value.trim();

        /* to enable numbers with commas and other local stuff beyond strict numbers we
         * use use numeral (glu/number) to create a number that is not negative and
         * check that it worked */
        // eslint-disable-next-line no-restricted-globals
        return !value || (!isNaN(Number(value)) && Number(value) >= 0);
      }

      return false;
    },

    maxLength(key, maxLength, data) {
      if (maxLength === false) {
        return true;
      }

      let value = data[key];

      if (typeof value === 'number') {
        value = `${value}`;
      }

      return !value || value.length <= maxLength;
    },

    /**
     * Ensures the value is less than or equal to the max value supplied as a parameter.
     *
     * @param key
     * @param maxValue
     * @param data
     * @return {*|boolean}
     */
    maxValue(key, maxValue, data) {
      if (maxValue === false) {
        return true;
      }

      const value = data[key];

      return (!value && value !== 0) || value <= maxValue;
    },

    /**
     * Ensures the value is less than the max value supplied as a parameter.
     * This version excludes the max value as valid so that the validation will
     * not pass if the value === the max value.
     *
     * @param key
     * @param maxValue
     * @param data
     * @return {*|boolean}
     */
    maxValueExclusive(key, maxValue, data) {
      if (maxValue === false) {
        return true;
      }

      const value = data[key];

      return (!value && value !== 0) || value < maxValue;
    },

    minLength(key, minLength, data) {
      let value = data[key];

      if (typeof value === 'number') {
        value = `${value}`;
      }

      return !value || value.length >= minLength;
    },

    /**
     * Ensures the value is greater than or equal to the min value supplied as a parameter.
     *
     * @param key
     * @param minValue
     * @param data
     * @return {*|boolean}
     */
    minValue(key, minValue, data) {
      if (minValue === false) {
        return true;
      }

      const value = data[key];

      return (!value && value !== 0) || value >= minValue;
    },

    /**
     * Ensures the value is greater than the min value supplied as a parameter.
     * This version excludes the min value as valid so that the validation will
     * not pass if the value === the min value.
     *
     * @param key
     * @param minValue
     * @param data
     * @return {*|boolean}
     */
    minValueExclusive(key, minValue, data) {
      if (minValue === false) {
        return true;
      }

      const value = data[key];

      return (!value && value !== 0) || value > minValue;
    },

    /**
     * Validates that the given value is present and not only whitespace
     * @param {string} key - The name of the attribute to check
     * @param {boolean} isRequired - Whether or not the field is required
     * @param {Object} data - The hash of data where the value is found
     * @returns {boolean|String} if enabled, returns whether the value is present; if disabled
     *  and not present, returns a special string to prevent execution of other validators
     */
    required(key, isRequired, data) {
      const value = data[key];
      const valueNotEmpty = hasValue(value);

      if (isRequired) {
        return valueNotEmpty;
      }

      if (!isRequired && !valueNotEmpty) {
        /* if its not required and it has no value return the special ignore all validators
         * value to disable all other validators after this one */
        return validationProcessor.IGNORE_ALL_OTHER_VALIDATORS;
      }

      return true;
    },

    us10DigitPhone(key, enabled, data) {
      if (!enabled || !data[key]) {
        return true;
      }

      return patterns.us10DigitPhone.test(data[key]) || !data[key];
    },

    usZipCode(key, enabled, data) {
      if (!enabled || !data[key]) {
        return true;
      }

      return patterns.usZip.test(data[key]);
    }
  },

  patterns
};

export default validators;
